import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, DEFAULT_IMAGE, PAGE_META, breadcrumbSchema } from "@/seo/pageMeta";
import blogMeta from "@/seo/blogMeta.json";
import caseMeta from "@/seo/caseMeta.json";

const upsert = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const setName = (name, content) =>
  upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  }, { content });

const setProp = (prop, content) =>
  upsert(`meta[property="${prop}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", prop);
    return m;
  }, { content });

const blogSchema = (slug, b) => {
  const url = `${SITE_URL}/blog/${slug}`;
  return [
    {
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: b.h1, description: b.description, image: b.image || DEFAULT_IMAGE,
      author: { "@type": "Person", "@id": `${SITE_URL}/about/daniel-rivas#person`, name: "Daniel Rivas", url: `${SITE_URL}/about/daniel-rivas` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      datePublished: b.datePublished, dateModified: b.dateModified,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      articleSection: b.category,
    },
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: "Blog", url: `${SITE_URL}/blog` },
      { name: b.h1, url },
    ]),
  ];
};

const caseSchema = (slug, c) => {
  const url = `${SITE_URL}/case-studies/${slug}`;
  return [
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: c.h1, description: c.description, image: c.image || DEFAULT_IMAGE,
      author: { "@id": `${SITE_URL}/#organization` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    },
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: "Case Studies", url: `${SITE_URL}/case-studies` },
      { name: c.h1, url },
    ]),
  ];
};

const resolveMeta = (path) => {
  const direct = PAGE_META[path];
  if (direct) return direct;
  const blog = path.match(/^\/blog\/([^/]+)$/);
  if (blog && blogMeta[blog[1]]) {
    const b = blogMeta[blog[1]];
    return { title: b.title, description: b.description, image: b.image || DEFAULT_IMAGE, schema: blogSchema(blog[1], b) };
  }
  const cs = path.match(/^\/case-studies\/([^/]+)$/);
  if (cs && caseMeta[cs[1]]) {
    const c = caseMeta[cs[1]];
    return { title: c.title, description: c.description, image: c.image || DEFAULT_IMAGE, schema: caseSchema(cs[1], c) };
  }
  return null;
};

export const MetaManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.replace(/\/$/, "") || "/";
    if (path.startsWith("/admin")) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      return;
    }
    const meta = resolveMeta(path);
    document.head.querySelectorAll('script[data-mm="1"]').forEach((el) => el.remove());
    if (!meta) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      return;
    }

    const canonicalUrl = path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
    upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, { href: canonicalUrl });
    setProp("og:url", canonicalUrl);

    document.title = meta.title;
    setName("description", meta.description);
    setProp("og:title", meta.title);
    setProp("og:description", meta.description);
    setName("twitter:title", meta.title);
    setName("twitter:description", meta.description);
    if (meta.image) {
      setProp("og:image", meta.image);
      setName("twitter:image", meta.image);
    }
    (meta.schema || []).forEach((schemaObj) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.mm = "1";
      el.text = JSON.stringify(schemaObj);
      document.head.appendChild(el);
    });
  }, [pathname]);

  return null;
};
